import React, {useEffect, useState} from "react";
import {useNavigate, useParams} from "react-router-dom";
import {useSelector} from "react-redux";
import moment from "moment";

import Page from "@/components/Page";
import Loading from "@/components/Loading";
import Breadcrumbs from "@/components/Breadcrumbs";
import MainActions from "@/components/MainActions";
import Alert from "@mui/material/Alert";
import VLFCard from "@/components/Card";
import VLFDatePicker from "@/components/Input/DatePicker";
import VLFSelectNew from "@/components/Input/SelectNew";
import VLFRadio from "@/components/Input/Radio";

import {pipelineAccess} from "@/utils/scoutingPipeline";
import {getDsContext} from "@/utils/dsContext";
import {PlayerAPI} from "@/api/player";
import {PlayerRoleAPI} from "@/api/player_role";

import ArrowBack from "@mui/icons-material/ArrowBack";
import SaveIcon from "@mui/icons-material/Save";

import {
	checkRequiredFields,
	getTextField,
	getTextAreaField,
} from "@/utils/functions";
import {
	DEFAULT_CATEGORY_ID,
	SUCCESSFUL_OPERATION,
	ERROR_OPERATION,
	AREA_OPTIONS,
	prospectPlayerStatus,
	sideOptions,
} from "@/utils/constants";

import "./index.scss";
import {useTranslation} from "react-i18next";

const HandleScoutingPlayerPage = (props) => {
	const {edit, handleNotification} = props;

	const navigate = useNavigate();
	const {id} = useParams();
	const seasonId = useSelector((state) => state?.season?.id);
	const {t} = useTranslation();

	const [loading, setLoading] = useState(Boolean(edit));
	const [rolesLoaded, setRolesLoaded] = useState(false);
	const [sourceError, setSourceError] = useState(false);
	const context = getDsContext();
	const [errors, setErrors] = useState([]);
	const [fields, setFields] = useState({});
	const [playerRolesList, setPlayerRolesList] = useState([]);

	const loadPlayerRoles = async () => {
		try {
			const response = await PlayerRoleAPI.list();
			if (context !== getDsContext()) return;
			setPlayerRolesList(response || []);
		} catch {
			setSourceError(true);
			handleNotification(ERROR_OPERATION, "error");
		} finally {
			setRolesLoaded(true);
		}
	};

	const getPlayer = async () => {
		setLoading(true);
		try {
			const response = await PlayerAPI.getById(id, {seasonId});
			if (context !== getDsContext()) return;
			response.dateOfBirth =
				moment(response.dateOfBirth)?.year() > 1900
					? response.dateOfBirth
					: null;

			if (response.prospectStatus) {
				response.prospectPlayerStatus = {
					value: response.prospectStatus,
					label: prospectPlayerStatus(t)?.filter(
						(s) => s.value === response.prospectStatus,
					)?.[0]?.label,
				};
			}

			response.playerPrimaryRole = response.goalkeeper
				? AREA_OPTIONS.find((o) => o.value === "Goalkeeper") || null
				: AREA_OPTIONS.find((o) => o.label === response.primaryRole) || null;

			const currentSecondaryRole = playerRolesList.find(
				(o) => o.name === response.secondaryRole,
			);
			response.playerSecondaryRole = response.secondaryRole
				? {
						value: currentSecondaryRole?.id || response.secondaryRole,
						label: response.secondaryRole,
					}
				: null;

			response.currentInternalRole =
				playerRolesList
					.filter((r) => r.id === response.internalRoleId)
					.map(roleOption)[0] || null;
			response.currentAlternativeRoles = (response.alternativeRoleIds || [])
				.map((id) => playerRolesList.find((r) => r.id === id))
				.filter(Boolean)
				.map(roleOption);
			response.currentAttitudes = (response.attitudes || []).map((value) => ({
				value,
				label: value,
			}));
			response.currentSomatotype = response.somatotype
				? {value: response.somatotype, label: response.somatotype}
				: null;
			response.currentProportions = response.proportions
				? {value: response.proportions, label: response.proportions}
				: null;
			setFields(response);
		} catch (error) {
			setSourceError(true);
			handleNotification(ERROR_OPERATION, "error");
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		loadPlayerRoles();
	}, []);

	useEffect(() => {
		if (edit && rolesLoaded) {
			getPlayer();
		}
	}, [edit, id, seasonId, rolesLoaded]); // eslint-disable-line react-hooks/exhaustive-deps

	const roleOption = (r) => ({
		value: r.id,
		label: r.code ? `${r.code} · ${r.name}` : r.name,
	});
	const getRequiredFields = () => {
		return ["lastName", "playerPrimaryRole"];
	};

	const savePlayer = async () => {
		const errors = checkRequiredFields(getRequiredFields(), fields);
		setErrors(errors);

		if (!errors.length) {
			let body = Object.assign({}, fields);
			const isGoalkeeper = fields?.playerPrimaryRole?.value === "Goalkeeper";
			body = {
				...fields,
				internalRoleId: fields.currentInternalRole?.value || null,
				alternativeRoleIds: (fields.currentAlternativeRoles || []).map(
					(r) => r.value,
				),
				attitudes: (fields.currentAttitudes || []).map((r) => r.value),
				somatotype: fields.currentSomatotype?.value || null,
				proportions: fields.currentProportions?.value || null,
				playerCategoryId: edit
					? fields.playerCategoryId ?? null
					: DEFAULT_CATEGORY_ID,
				firstName: fields.firstName ? fields.firstName : "",
				prospectStatus: fields?.prospectPlayerStatus?.value,
				goalkeeper: isGoalkeeper,
				primaryRole: fields?.playerPrimaryRole?.label || null,
				secondaryRole: isGoalkeeper
					? null
					: fields?.playerSecondaryRole?.label || null,
				dateOfBirth: fields.dateOfBirth
					? moment(fields.dateOfBirth).format("YYYY-MM-DD")
					: undefined,
			};

			setLoading(true);

			try {
				edit ? await PlayerAPI.update(id, body) : await PlayerAPI.create(body);
				navigate("/scouting/players");
				handleNotification(SUCCESSFUL_OPERATION, "success");
			} catch (error) {
				handleNotification(ERROR_OPERATION, "error");
				setLoading(false);
			}
		}
	};

	const handleSubmit = (event) => {
		event.preventDefault();
		savePlayer();
	};

	const goBack = () => {
		navigate("/scouting/players");
	};

	const mainActions = [
		{icon: <ArrowBack />, title: "Indietro", onClick: goBack, color: "info"},
		{
			icon: <SaveIcon />,
			title: "Salva",
			onClick: handleSubmit,
			disabled:
				sourceError ||
				!rolesLoaded ||
				checkRequiredFields(getRequiredFields(), fields).length
					? true
					: false,
		},
	];

	const defaultTextField = {
		values: fields,
		setValues: setFields,
		errors,
		setErrors,
	};

	const playerLabel =
		fields?.firstName || fields?.lastName
			? (fields.firstName || "") + " " + (fields.lastName || "")
			: "";

	const breadcrumbsElements = [
		{label: "Giocatori osservati", path: "/scouting/players"},
	];
	if (edit) {
		breadcrumbsElements.push(
			{label: "/"},
			{
				label: playerLabel,
				path: "/scouting/players/" + id,
			},
		);
	}

	return (
		<Page>
			<Loading visible={loading} />
			<Breadcrumbs
				elements={breadcrumbsElements}
				current={edit ? "Modifica" : "Nuovo giocatore"}
				actions={<MainActions actions={mainActions} />}
			/>
			{sourceError && (
				<Alert severity="error">
					Dati della scheda non disponibili. Ricarica la pagina per riprovare.
				</Alert>
			)}
			<div className="sheet-container">
				<div className="row no-margin">
					<div className="col-sm-12 padding-small">
						<VLFCard
							title="Informazioni principali"
							content={
								<div className="row no-margin">
									<div className="col-12 col-md-6 padding-small">
										{getTextField({
											...defaultTextField,
											fieldKey: "firstName",
											label: t("name"),
										})}
									</div>
									<div className="col-12 col-md-6 padding-small">
										{getTextField({
											...defaultTextField,
											fieldKey: "lastName",
											label: "Cognome",
											required: true,
										})}
									</div>
									<div className="col-12 col-md-6 padding-small">
										<VLFDatePicker
											label="Data di nascita"
											value={fields.dateOfBirth}
											onChange={(value) => {
												setFields({...fields, dateOfBirth: value});
											}}
										/>
									</div>
									<div className="col-12 col-md-6 padding-small">
										{getTextField({
											...defaultTextField,
											type: "number",
											fieldKey: "height",
											label: "Altezza (cm)",
										})}
									</div>
									<div className="col-12 col-md-6 padding-small">
										{getTextField({
											...defaultTextField,
											type: "number",
											fieldKey: "weight",
											label: "Peso (kg)",
										})}
									</div>
									{/* <div className="col-12 col-md-6 padding-small">
										<VLFSelectNew
											options={prospectPlayerStatus}
											sorted={false}
											value={fields.prospectPlayerStatus}
											onChange={(newValue) => {
												setFields({...fields, prospectPlayerStatus: newValue});
											}}
											label="Status"
										/>
									</div> */}
									<div className="col-12 col-md-6 padding-small">
										{getTextField({
											...defaultTextField,
											fieldKey: "favoriteFoot",
											label: "Piede principale",
										})}
									</div>
									<div className="col-12 padding-small">
										{getTextAreaField({
											...defaultTextField,
											fieldKey: "notes",
											label: "Note",
											minRows: 3,
											maxRows: 3,
										})}
									</div>
								</div>
							}
						/>
					</div>
				</div>
				<div className="row no-margin mt-2">
					<div className="col-sm-12 padding-small">
						<VLFCard
							title="Informazioni ruolo"
							content={
								<div className="row no-margin">
									<div className="col-sm-12 col-md-6">
										<VLFSelectNew
											options={AREA_OPTIONS}
											sorted={false}
											value={fields.playerPrimaryRole}
											onChange={(newValue) => {
												setFields({
													...fields,
													playerPrimaryRole: newValue,
													playerSecondaryRole: null,
												});
											}}
											label="Ruolo"
											required
										/>
									</div>
									{fields?.playerPrimaryRole?.value === "Goalkeeper" ? (
										<div className="col-sm-12 col-md-6">
											<VLFRadio
												name="Mano"
												value={fields.favoriteHand || ""}
												onChange={(event) => {
													setFields({
														...fields,
														favoriteHand: event.target.value,
													});
												}}
												options={sideOptions(t)}
												label="Mano preferita"
												size="small"
											/>
										</div>
									) : fields?.playerPrimaryRole ? (
										<div className="col-sm-12 col-md-6">
											<VLFSelectNew
												options={playerRolesList
													.filter(
														(r) =>
															r.active !== false ||
															r.name === fields.secondaryRole,
													)
													.map((r) => ({
														value: r.id,
														label: r.name,
													}))}
												value={fields.playerSecondaryRole || null}
												onChange={(newValue) => {
													setFields({
														...fields,
														playerSecondaryRole: newValue,
													});
												}}
												label="Ruolo specifico"
											/>
										</div>
									) : null}
								</div>
							}
						/>
					</div>
				</div>
				<div className="row no-margin mt-2">
					<div className="col-12 padding-small">
						<VLFCard
							title="Valutazione della struttura scouting"
							content={
								<div className="row no-margin">
									{[
										[
											"currentInternalRole",
											"Ruolo interno",
											playerRolesList
												.filter(
													(r) =>
														r.active !== false ||
														r.id === fields.internalRoleId,
												)
												.map(roleOption),
											false,
										],
										[
											"currentAlternativeRoles",
											"Ruoli alternativi (ordine di preferenza)",
											playerRolesList
												.filter(
													(r) =>
														(r.active !== false ||
															(fields.alternativeRoleIds || []).includes(
																r.id,
															)) &&
														r.id !== fields.currentInternalRole?.value,
												)
												.map(roleOption),
											true,
										],
										[
											"currentAttitudes",
											"Attitudini",
											["D", "R", "AR", "AF"].map((value) => ({
												value,
												label: value,
											})),
											true,
										],
										[
											"currentSomatotype",
											"Somatotipo",
											[
												{value: "EC", label: "EC · Ectomorfo"},
												{value: "ME", label: "ME · Mesomorfo"},
												{value: "EN", label: "EN · Endomorfo"},
												{value: "M-EC", label: "M-EC · Meso-ectomorfo"},
												{value: "M-EN", label: "M-EN · Meso-endomorfo"},
											],
											false,
										],
										[
											"currentProportions",
											"Proporzioni",
											[
												{value: "LN", label: "LN · Longilineo"},
												{value: "NL", label: "NL · Normolineo"},
												{value: "BR", label: "BR · Brevilineo"},
											],
											false,
										],
									].map(([key, label, options, multiple]) => (
										<div key={key} className="col-12 col-md-6 padding-small">
											<VLFSelectNew
												label={label}
												options={options}
												sorted={false}
												isMulti={multiple}
												isDisabled={!pipelineAccess().canWrite}
												value={fields[key] || (multiple ? [] : null)}
												onChange={(value) =>
													setFields({
														...fields,
														[key]: value,
														...(key === "currentInternalRole"
															? {
																	currentAlternativeRoles: (
																		fields.currentAlternativeRoles || []
																	).filter((r) => r.value !== value?.value),
																}
															: {}),
													})
												}
											/>
										</div>
									))}
									{fields.providerRole && (
										<div className="col-12 padding-small">
											Reparto provider: {fields.providerRole}
										</div>
									)}
								</div>
							}
						/>
					</div>
				</div>
			</div>
		</Page>
	);
};

export default function HandleScoutingPlayerContext(props) {
	useSelector((s) => s.user);
	useSelector((s) => s.token);
	useSelector((s) => s.season?.id);
	const {id} = useParams();
	return (
		<HandleScoutingPlayerPage
			key={`${getDsContext()}:${id || "new"}`}
			{...props}
		/>
	);
}
